import { ApplicationRef, inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Closes open PrimeNG popup overlays when the user navigates away.
 *
 * TabRouteReuseStrategy detaches the leaving page instead of destroying it, so
 * ngOnDestroy never runs on cached tab views. Popups with appendTo="body" move
 * their overlay DOM into document.body; without an explicit dismiss, that markup
 * stays visible over the newly activated tab.
 *
 * dismissAll() dispatches a synthetic click on document.body (not document):
 * - PrimeNG popups bind their own document click listeners and call hide() when
 *   the target is outside their container, which keeps component state consistent
 *   and runs restoreOverlayAppend() / ZIndexUtils.clear().
 * - body is an HTMLElement, so app handlers that call event.target.closest(...) do
 *   not throw (document would).
 * - body is never contained by an overlay container, so every outside-check resolves
 *   to "outside".
 *
 * ApplicationRef.tick() is required because a detached view is no longer change-
 * detected; hide() must flush before Angular detaches the route.
 */
@Injectable({ providedIn: 'root' })
export class OverlayDismissService {
    private readonly router = inject(Router);
    private readonly appRef = inject(ApplicationRef);

    constructor() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe(() => this.dismissAll());
    }

    dismissAll(): void {
        document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        this.appRef.tick();
    }
}
